import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Collapse, List, ListItem, ListItemIcon, ListItemText, Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import ViewWeekOutlinedIcon from "@material-ui/icons/ViewWeekOutlined";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";
import CampaignIcon from "@material-ui/icons/SendOutlined";
import ListIcon from "@material-ui/icons/ListAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AssignmentTurnedInOutlinedIcon from "@material-ui/icons/AssignmentTurnedInOutlined";
import CakeOutlinedIcon from "@material-ui/icons/CakeOutlined";
import NotificationsNoneIcon from "@material-ui/icons/NotificationsNone";
import { i18n } from "../translate/i18n";
import { canAccessCrm } from "../utils/crmAccess";
import { getCrmColors } from "../theme/crmColors";

const matchesPath = (pathname, route) => pathname === route || pathname.startsWith(`${route}/`);

const useStyles = makeStyles(theme => ({
  root: { margin: theme.spacing(0.5, 0) },
  trigger: {
    minHeight: 44,
    width: "calc(100% - 16px)",
    margin: "0 8px",
    padding: "4px 8px",
    border: 0,
    borderRadius: 8,
    backgroundColor: "transparent",
    color: theme.mode === "light" ? "#475569" : "#FFFFFF",
    textAlign: "left",
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: theme.mode === "light" ? "#eaf3ff" : getCrmColors(theme, "dark").selected,
      color: theme.mode === "light" ? "#245b92" : "#FFFFFF",
      boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
    },
  },
  triggerIcon: { minWidth: 48, color: "inherit" },
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 36,
    width: 36,
    borderRadius: "50%",
    backgroundColor: theme.mode === "light" ? "rgba(120,120,120,0.1)" : getCrmColors(theme, "dark").raised,
    "& svg": { fontSize: 22 },
  },
  title: { fontSize: 14, fontWeight: 700 },
  children: {
    margin: "4px 12px 8px 33px",
    padding: "0 0 0 8px",
    borderLeft: `1px solid ${theme.mode === "light" ? "#d9e2ef" : getCrmColors(theme, "dark").border}`,
  },
  item: {
    minHeight: 38,
    padding: "6px 8px",
    margin: "2px 0",
    borderRadius: 6,
    color: theme.mode === "light" ? "#526174" : "#FFFFFF",
    "&.Mui-selected, &.Mui-selected:hover": {
      color: theme.mode === "light" ? "#245b92" : "#FFFFFF",
      backgroundColor: theme.mode === "light" ? "#eaf3ff" : getCrmColors(theme, "dark").selected,
    },
    "&.Mui-selected $label": { fontWeight: 700 },
  },
  itemIcon: { minWidth: 27, color: "inherit", "& svg": { fontSize: 18 } },
  label: { fontSize: 13, lineHeight: 1.4, whiteSpace: "normal" },
  campaigns: { margin: "0 0 4px 13px", paddingLeft: 10 },
  campaignTrigger: { width: "100%", border: 0, backgroundColor: "transparent", textAlign: "left" },
  chevron: { fontSize: 18, flexShrink: 0 },
}));

const CrmMenu = ({ user, collapsed, onExpand, showKanban, showCampaigns }) => {
  const classes = useStyles();
  const location = useLocation();
  const pathname = location.pathname.toLowerCase();
  const isAdmin = user.profile === "admin";
  const canViewReports = isAdmin || (user.profile === "user" && user.showDashboard === "enabled");
  const isAssessments = ["/", "/dashboard"].includes(pathname) && new URLSearchParams(location.search).get("tab") === "NPS";
  const isCampaignRoute = ["/campaigns", "/campaign", "/contact-lists", "/campaigns-config"].some(route => matchesPath(pathname, route));

  const items = [
    { to: "/kanban", label: "kanban", icon: <ViewWeekOutlinedIcon />, visible: isAdmin && showKanban, active: pathname === "/kanban" || pathname === "/tagskanban" },
    { to: "/crm/attention", label: "attention", icon: <NotificationsNoneIcon />, visible: showKanban, active: matchesPath(pathname, "/crm/attention") },
    { to: "/crm/relationship", label: "relationship", icon: <PeopleOutlineIcon />, visible: showKanban, active: matchesPath(pathname, "/crm/relationship") },
    { to: "/crm/birthdays", label: "birthdays", icon: <CakeOutlinedIcon />, visible: showKanban, active: matchesPath(pathname, "/crm/birthdays") },
    { to: "/crm/tasks", label: "tasks", icon: <AssignmentTurnedInOutlinedIcon />, visible: showKanban, active: matchesPath(pathname, "/crm/tasks") },
    { to: "/dashboard?tab=NPS", label: "assessments", icon: <StarBorderIcon />, visible: canViewReports, active: isAssessments },
    { to: "/reports", label: "reports", icon: <AssessmentOutlinedIcon />, visible: canViewReports, active: matchesPath(pathname, "/reports") },
  ].filter(item => item.visible);
  const canViewCampaigns = isAdmin && showCampaigns;
  const isActive = items.some(item => item.active) || (canViewCampaigns && isCampaignRoute);
  const [open, setOpen] = useState(false);
  const [campaignsOpen, setCampaignsOpen] = useState(isCampaignRoute);

  useEffect(() => {
    if (isCampaignRoute) setCampaignsOpen(true);
  }, [isCampaignRoute]);

  const renderLink = (to, label, icon, active) => (
    <ListItem
      key={to}
      button
      component={RouterLink}
      to={to}
      selected={active}
      aria-current={active ? "page" : undefined}
      className={classes.item}
    >
      <ListItemIcon className={classes.itemIcon}>{icon}</ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ className: classes.label }} />
    </ListItem>
  );

  if (!canAccessCrm(user) || (!items.length && !canViewCampaigns)) return null;

  return (
    <div className={classes.root}>
      <Tooltip title={collapsed ? "CRM" : ""} placement="right">
        <ListItem
          button
          component="button"
          type="button"
          id="crm-menu-trigger"
          aria-label="CRM"
          aria-expanded={!collapsed && open}
          aria-controls="crm-menu-items"
          selected={isActive}
          className={classes.trigger}
          onClick={() => {
            if (collapsed) onExpand();
            setOpen(collapsed ? true : !open);
          }}
        >
          <ListItemIcon className={classes.triggerIcon}>
            <span className={classes.icon}><PeopleOutlineIcon /></span>
          </ListItemIcon>
          {!collapsed && <>
            <ListItemText primary="CRM" primaryTypographyProps={{ className: classes.title }} />
            {open ? <ExpandLessIcon className={classes.chevron} /> : <ExpandMoreIcon className={classes.chevron} />}
          </>}
        </ListItem>
      </Tooltip>
      <Collapse in={!collapsed && open} timeout="auto" unmountOnExit>
        <List component="div" id="crm-menu-items" aria-labelledby="crm-menu-trigger" className={classes.children} disablePadding>
          {items.map(item => renderLink(item.to, i18n.t(`mainDrawer.crm.${item.label}`), item.icon, item.active))}
          {canViewCampaigns && <>
            <ListItem
              button
              component="button"
              type="button"
              className={`${classes.item} ${classes.campaignTrigger}`}
              selected={isCampaignRoute}
              aria-expanded={campaignsOpen}
              aria-controls="crm-campaign-items"
              onClick={() => setCampaignsOpen(previous => !previous)}
            >
              <ListItemIcon className={classes.itemIcon}><CampaignIcon /></ListItemIcon>
              <ListItemText primary={i18n.t("mainDrawer.crm.campaigns")} primaryTypographyProps={{ className: classes.label }} />
              {campaignsOpen ? <ExpandLessIcon className={classes.chevron} /> : <ExpandMoreIcon className={classes.chevron} />}
            </ListItem>
            <Collapse in={campaignsOpen} timeout="auto" unmountOnExit>
              <List component="div" id="crm-campaign-items" className={classes.campaigns} disablePadding>
                {renderLink("/campaigns", i18n.t("mainDrawer.crm.campaignList"), <ListIcon />, matchesPath(pathname, "/campaigns") || matchesPath(pathname, "/campaign"))}
                {renderLink("/contact-lists", i18n.t("mainDrawer.crm.contactLists"), <PeopleOutlineIcon />, matchesPath(pathname, "/contact-lists"))}
                {renderLink("/campaigns-config", i18n.t("mainDrawer.crm.campaignSettings"), <SettingsOutlinedIcon />, pathname === "/campaigns-config")}
              </List>
            </Collapse>
          </>}
        </List>
      </Collapse>
    </div>
  );
};

export default CrmMenu;
